import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { analytics } from '../../../services/analytics';
import type { BingoResultToShowType, MetagameBingoEvent } from '../../../stores/bingo/types';
import { useStores } from '../../../stores/useStores';

const ANIMATION_READY_DELAY_MS = 3000;

export type AnimationReadyRef = MutableRefObject<boolean>;

export function useBingoAnimationReady(): AnimationReadyRef {
  const animationReadyRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      animationReadyRef.current = true;
    }, ANIMATION_READY_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  return animationReadyRef;
}

export function useBingoWidgetCallbacks() {
  const { bingoStore } = useStores();

  const onPurchaseClick = useCallback(() => {
    bingoStore.openBingoStore();
  }, [bingoStore]);

  const onEnterLiveEventClick = (event: MetagameBingoEvent) => {
    const location = event?.coinType === 'CASH' ? 'sweeps_bingo' : 'cc_bingo';
    void analytics.sendEvent('enter_live_button_click', {
      screen_name: 'lobby',
      location: location,
      event_action: 'click',
      event_category: 'metagame_bingo',
    });

    void bingoStore.enterLiveGame();
  };

  const onResultClick = (event: BingoResultToShowType) => {
    void bingoStore.acknowledgeBingoEvent(event.gameId);
    const { gameId, totalUserReward, coinType, numOfUserWinningCards } = event;
    if (gameId && totalUserReward !== undefined && coinType) {
      bingoStore.openScratchCardDialog({
        totalUserReward,
        coinType,
        gameId,
        numOfUserWinningCards,
      });
    }
  };

  return { onPurchaseClick, onEnterLiveEventClick, onResultClick };
}
